import type { ReactNode } from 'react'
import {
  CircleAlert,
  CircleCheck,
  CircleDashed,
  TriangleAlert,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'
import type {
  PackagingItemSvhcAuditLatest,
  PackagingItemSvhcAuditStatus,
} from '@/shared/hooks/api/packagingItemsOverview'
import { formatRunTimestamp } from './PackagingFieldCrossCheckRunResults'
import {
  packagingItemSvhcAuditStatusLabelKey,
  packagingSvhcAuditVerdictLabelKey,
} from './packagingItemSvhcAuditFilter'

type PackagingItemSvhcAuditCellProps = {
  svhcAuditStatus: PackagingItemSvhcAuditStatus
  svhcAuditLatest: PackagingItemSvhcAuditLatest | null
  svhcAuditLastRunAt?: string | null
}

function SvhcAuditTableHoverCell({
  ariaLabel,
  icon,
  children,
}: {
  ariaLabel: string
  icon: ReactNode
  children: ReactNode
}) {
  return (
    <HoverCard openDelay={200}>
      <HoverCardTrigger asChild>
        <button
          type='button'
          className='inline-flex rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
          aria-label={ariaLabel}
          onClick={(event) => event.stopPropagation()}
        >
          {icon}
        </button>
      </HoverCardTrigger>
      <HoverCardContent align='start' className='w-80 space-y-2 text-sm'>
        {children}
      </HoverCardContent>
    </HoverCard>
  )
}

function svhcAuditStatusIcon(status: PackagingItemSvhcAuditStatus): ReactNode {
  if (status === 'passed') {
    return (
      <CircleCheck
        className='size-4 text-green-600 dark:text-green-500'
        aria-hidden
      />
    )
  }
  if (status === 'failed') {
    return <CircleAlert className='size-4 text-destructive' aria-hidden />
  }
  if (status === 'flagged') {
    return (
      <TriangleAlert
        className='size-4 text-amber-600 dark:text-amber-500'
        aria-hidden
      />
    )
  }
  return <CircleDashed className='size-4 text-muted-foreground' aria-hidden />
}

export function PackagingItemSvhcAuditCell({
  svhcAuditStatus,
  svhcAuditLatest,
  svhcAuditLastRunAt,
}: PackagingItemSvhcAuditCellProps) {
  const { t, i18n } = useTranslation('packagingSuppliers')

  const statusLabel =
    svhcAuditStatus === 'not_audited'
      ? t('list.svhcAudit.notAudited')
      : t(packagingItemSvhcAuditStatusLabelKey(svhcAuditStatus))

  const runLabel =
    svhcAuditLastRunAt != null && svhcAuditLastRunAt !== ''
      ? formatRunTimestamp(svhcAuditLastRunAt, i18n.language)
      : null
  const showNotAuditedStatus =
    svhcAuditStatus === 'not_audited' && svhcAuditLatest == null
  const showStatusFallback =
    svhcAuditLatest == null && svhcAuditStatus !== 'not_audited'

  return (
    <SvhcAuditTableHoverCell
      ariaLabel={statusLabel}
      icon={svhcAuditStatusIcon(svhcAuditStatus)}
    >
      {showNotAuditedStatus && <p>{statusLabel}</p>}
      {svhcAuditLatest != null && (
        <>
          <p className='font-medium'>
            {t(packagingSvhcAuditVerdictLabelKey(svhcAuditLatest.verdict))}
          </p>
          {runLabel != null && (
            <p className='text-muted-foreground'>{runLabel}</p>
          )}
          <p className='text-muted-foreground line-clamp-4'>
            {svhcAuditLatest.reasoningEn}
          </p>
        </>
      )}
      {showStatusFallback && <p>{statusLabel}</p>}
    </SvhcAuditTableHoverCell>
  )
}
